import { LogOut } from 'lucide-react';
import * as React from 'react';

import { Avatar } from '../../atoms/Avatar';

export interface UserProfilePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  /** Ref to the trigger element so clicks on it are ignored by the outside-click handler. */
  triggerRef: React.RefObject<HTMLElement | null>;
  initials: string;
  displayName: string;
  email: string;
  onLogout: () => void;
}

export const UserProfilePopover: React.FC<UserProfilePopoverProps> = ({
  isOpen,
  onClose,
  triggerRef,
  initials,
  displayName,
  email,
  onLogout,
}) => {
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const logoutButtonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      onClose();
      triggerRef.current?.focus();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, triggerRef]);

  // Move focus to the Log out button when the popover opens
  React.useEffect(() => {
    if (isOpen) {
      logoutButtonRef.current?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLogoutClick = () => {
    // Close before running the callback so click-outside does not race with navigation
    onClose();
    onLogout();
  };

  return (
    <div
      ref={popoverRef}
      role="menu"
      aria-label="User menu"
      className="absolute z-50 overflow-hidden right-0 mt-3 w-[280px] bg-dawn-white border border-grey-200 rounded-lg shadow-[0px_8px_24px_rgba(0,0,0,0.12),0px_2px_8px_rgba(0,0,0,0.08)]"
      style={{ top: '100%' }}
    >
      {/* User info */}
      <div className="px-4 py-[14px] bg-grey-100 border-b border-grey-200">
        <div className="flex items-center gap-3">
          <Avatar initials={initials} size="lg" />
          <div className="flex flex-col">
            <span className="text-[14px] font-medium text-dawn-black font-outfit">
              {displayName}
            </span>
            <span className="text-[12px] text-grey-400 font-outfit">{email}</span>
          </div>
        </div>
      </div>

      {/* Logout row */}
      <button
        ref={logoutButtonRef}
        type="button"
        role="menuitem"
        onClick={handleLogoutClick}
        className="w-full flex items-center gap-3 px-4 py-[10px] text-dawn-black hover:bg-grey-100 transition-colors font-outfit text-[13px] font-medium cursor-pointer"
      >
        <LogOut className="w-4 h-4" />
        Log out
      </button>
    </div>
  );
};

UserProfilePopover.displayName = 'UserProfilePopover';
